// Independent of Dawn's facets.js: native grid replacements trigger a section refresh.
if (!customElements.get('cta-collection-filters')) {
  customElements.define('cta-collection-filters', class extends HTMLElement {
    connectedCallback() {
      this.renderedUrl = window.location.pathname + window.location.search;
      this.pendingUrl = null;
      this.setupRows();
      this.observer = new MutationObserver((mutations) => {
        const gridUpdated = mutations.some((mutation) =>
          mutation.target.id === 'ProductGridContainer' ||
          Array.from(mutation.addedNodes).some((node) => node.nodeType === 1 &&
            (node.id === 'ProductGridContainer' || node.querySelector('#ProductGridContainer')))
        );
        if (gridUpdated) this.refresh();
      });
      this.observer.observe(document.getElementById('MainContent') || document.body, {
        childList: true,
        subtree: true,
      });
    }

    disconnectedCallback() {
      this.rowEvents?.abort();
      this.rowResize?.disconnect();
      cancelAnimationFrame(this.rowFrame);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }

    setupRows() {
      this.rowEvents?.abort();
      this.rowResize?.disconnect();
      cancelAnimationFrame(this.rowFrame);
      this.rowEvents = new AbortController();
      const signal = this.rowEvents.signal;
      const update = () => {
        this.querySelectorAll('[data-row-id]').forEach((row) => {
          const track = row.querySelector('.cta-filter-buttons');
          const scrolling = getComputedStyle(track).overflowX === 'auto';
          row.classList.toggle('is-overflowing', scrolling && track.scrollWidth > track.clientWidth + 1);
          const rtl = getComputedStyle(track).direction === 'rtl';
          const position = Math.abs(track.scrollLeft);
          const end = track.scrollWidth - track.clientWidth;
          row.querySelector('[data-scroll="-1"]').disabled = rtl ? position >= end - 1 : position <= 1;
          row.querySelector('[data-scroll="1"]').disabled = rtl ? position <= 1 : position >= end - 1;
        });
      };
      const schedule = () => { cancelAnimationFrame(this.rowFrame); this.rowFrame = requestAnimationFrame(update); };
      this.rowResize = new ResizeObserver(schedule);
      this.querySelectorAll('.cta-filter-buttons').forEach((track) => {
        this.rowResize.observe(track);
        track.addEventListener('scroll', schedule, {passive:true, signal});
      });
      this.addEventListener('click', (event) => {
        const arrow = event.target.closest('[data-scroll]');
        if (!arrow || !this.contains(arrow)) return;
        const track = arrow.closest('[data-row-id]').querySelector('.cta-filter-buttons');
        track.scrollBy({left: Number(arrow.dataset.scroll) * track.clientWidth * .8,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      }, {signal});
      schedule();
    }

    async refresh() {
      const currentUrl = window.location.pathname + window.location.search;
      if (currentUrl === this.pendingUrl) return;
      this.controller?.abort();
      this.controller = null;
      this.pendingUrl = null;
      if (currentUrl === this.renderedUrl) {
        this.removeAttribute('aria-busy');
        this.removeAttribute('data-refresh-error');
        this.querySelector('.cta-filter-error')?.remove();
        this.querySelector('[data-cta-filter-rows]')?.removeAttribute('inert');
        return;
      }
      const controller = new AbortController();
      this.controller = controller;
      this.pendingUrl = currentUrl;
      this.setAttribute('aria-busy', 'true');
      this.querySelector('[data-cta-filter-rows]')?.setAttribute('inert', '');
      const url = new URL(currentUrl, window.location.origin);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Filter section request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = html.getElementById(this.id);
        if (!incoming || !incoming.querySelector('[data-cta-filter-rows]')) {
          throw new Error('Filter section response missing');
        }
        if (controller.signal.aborted || !this.isConnected || this.controller !== controller) return;
        const positions = new Map(Array.from(this.querySelectorAll('[data-row-id]')).map((row) =>
          [row.dataset.rowId, row.querySelector('.cta-filter-buttons').scrollLeft]
        ));
        this.innerHTML = incoming.innerHTML;
        this.setupRows();
        this.querySelectorAll('[data-row-id]').forEach((row) => {
          row.querySelector('.cta-filter-buttons').scrollLeft = positions.get(row.dataset.rowId) || 0;
        });
        this.renderedUrl = currentUrl;
        this.removeAttribute('data-refresh-error');
      } catch (error) {
        if (controller.signal.aborted || this.controller !== controller || !this.isConnected) return;
        // Do not let shoppers click stale filter links after a failed refresh.
        this.setAttribute('data-refresh-error', 'true');
        this.querySelector('.cta-filter-error')?.remove();
        const notice = document.createElement('p');
        notice.className = 'cta-filter-error page-width';
        notice.setAttribute('role', 'alert');
        notice.append(document.createTextNode(this.dataset.errorMessage + ' '));
        const link = document.createElement('a');
        link.href = currentUrl;
        link.textContent = this.dataset.reloadLabel;
        notice.append(link);
        this.append(notice);
      } finally {
        if (this.controller === controller) {
          this.pendingUrl = null;
          this.controller = null;
          this.removeAttribute('aria-busy');
          this.querySelector('[data-cta-filter-rows]')?.removeAttribute('inert');
        }
      }
    }
  });
}
